import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import { abrirParaImprimir } from '@/lib/imprimir';
import { Stethoscope, Search, AlertTriangle, Lock, FileText, Pill, Eye, Plus, X } from 'lucide-react';

// S127 — Consultorio: el expediente clínico.
//
// Esta pantalla está armada alrededor de dos cosas que el médico necesita ver
// SIN buscarlas:
//
//   1. Las alergias, arriba y en rojo, antes que cualquier otra cosa. Enterrar
//      una alergia dentro de una nota vieja es el error que mata gente.
//   2. Si la nota está abierta o cerrada. Una nota cerrada no se edita: se
//      corrige con una nota nueva que la referencia, y la pantalla lo dice con
//      todas sus letras en vez de dejar campos que parecen editables y no lo son.

function Alergias({ lista, grande }) {
  if (!lista || lista.length === 0) {
    return (
      <span className="text-sm text-slate-500">
        Sin alergias registradas —{' '}
        <span className="text-amber-700">no es lo mismo que «no tiene»</span>
      </span>
    );
  }
  return (
    <div className={`flex flex-wrap items-center gap-2 ${grande ? 'text-base' : 'text-sm'}`}>
      <AlertTriangle className={`text-rose-600 ${grande ? 'w-5 h-5' : 'w-4 h-4'}`} />
      <span className="font-semibold text-rose-700">Alergias:</span>
      {lista.map((a) => (
        <span key={a} className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 font-medium">
          {a}
        </span>
      ))}
    </div>
  );
}

function Etiquetas({ titulo, lista }) {
  if (!lista || lista.length === 0) return null;
  return (
    <div className="text-sm">
      <span className="text-slate-500">{titulo}: </span>
      <span className="text-slate-800">{lista.join(', ')}</span>
    </div>
  );
}

const MED_VACIO = {
  drug_name: '', presentation: '', dose: '', frequency: '',
  duration: '', route: 'Oral', instructions: '', is_controlled: false,
};

export default function ClinicDesk() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [cargando, setCargando] = useState(true);
  const [hoy, setHoy] = useState(null);
  const [lista, setLista] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [expediente, setExpediente] = useState(null);   // historia completa
  const [nuevaConsulta, setNuevaConsulta] = useState(null);
  const [dx, setDx] = useState(null);
  const [receta, setReceta] = useState(null);
  const [correccion, setCorreccion] = useState(null);
  const [accesos, setAccesos] = useState(null);
  // S140 -- abrir un expediente. El endpoint existia desde S127 y no lo
  // llamaba nadie: la pantalla mandaba a "Clientes y prospectos" y ahi no
  // habia nada que dar clic. Se podia entrar a un expediente ya abierto,
  // pero no habia forma de abrir el primero.
  const [eligiendo, setEligiendo] = useState(false);
  const [buscaPaciente, setBuscaPaciente] = useState('');
  const [pacientes, setPacientes] = useState([]);
  const [abriendo, setAbriendo] = useState(false);

  const cargarHoy = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/clinica/hoy');
      setHoy(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  const cargarLista = useCallback(async (texto) => {
    try {
      const { data } = await api.get('/tenant/clinica/expedientes', {
        params: texto ? { buscar: texto } : {},
      });
      setLista(data.expedientes || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  // Se busca entre los contactos del negocio, que es donde vive el paciente.
  useEffect(() => {
    if (!eligiendo || !buscaPaciente.trim()) { setPacientes([]); return undefined; }
    const t = setTimeout(() => {
      api.get('/tenant/crm/leads', { params: { search: buscaPaciente.trim(), page_size: 10 } })
        .then((r) => setPacientes(r.data?.items || []))
        .catch(() => setPacientes([]));
    }, 300);
    return () => clearTimeout(t);
  }, [eligiendo, buscaPaciente]);

  // Abrir es idempotente en el servidor: si esa persona ya tiene expediente
  // devuelve el suyo en vez de crear otro. Por eso aqui hay UN solo boton y no
  // uno de "crear" y otro de "abrir", que es donde se equivoca la gente.
  async function abrirDeContacto(persona) {
    setAbriendo(true);
    try {
      const { data } = await api.post('/tenant/clinica/expedientes',
                                      { crm_lead_id: persona.id });
      setEligiendo(false);
      setBuscaPaciente('');
      setPacientes([]);
      await Promise.all([cargarHoy(), cargarLista(busqueda)]);
      await abrirExpediente(data.expediente.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setAbriendo(false); }
  }

  // Un paciente nuevo que todavia no es contacto de nadie. Sin esto habria que
  // salir a otra pantalla, darlo de alta y volver -- que es justo el camino que
  // esta pantalla prometia y no existia.
  async function altaYExpediente() {
    const nombre = buscaPaciente.trim();
    if (!nombre) return;
    setAbriendo(true);
    try {
      const { data } = await api.post('/tenant/crm/leads', { display_name: nombre });
      await abrirDeContacto({ id: data.id || data.lead?.id });
    } catch (e) {
      toast.error(formatApiError(e));
      setAbriendo(false);
    }
  }

  useEffect(() => { cargarHoy(); cargarLista(''); }, [cargarHoy, cargarLista]);

  // Puente Agenda -> Expediente (Fase 6): abrir directo desde un enlace.
  useEffect(() => {
    const exp = sp.get('expediente');
    const lead = sp.get('lead');
    if (exp) { abrirExpediente(exp); }
    else if (lead) { abrirDeContacto({ id: lead }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function abrirExpediente(id) {
    try {
      const { data } = await api.get(`/tenant/clinica/expedientes/${id}`);
      setExpediente(data);
      setAccesos(null);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function abrirConsulta() {
    try {
      await api.post(`/tenant/clinica/expedientes/${expediente.expediente.id}/consultas`, {
        practitioner_name: nuevaConsulta.medico || null,
        practitioner_license: nuevaConsulta.cedula || null,
        reason: nuevaConsulta.motivo || null,
        vitals: limpiarVitales(nuevaConsulta),
      });
      toast.success('Consulta abierta.');
      setNuevaConsulta(null);
      await abrirExpediente(expediente.expediente.id);
      await cargarHoy();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function limpiarVitales(f) {
    const v = {};
    if (f.ta) v.ta = f.ta;
    if (f.fc) v.fc = Number(f.fc);
    if (f.temp) v.temp = Number(f.temp);
    if (f.peso) v.peso = Number(f.peso);
    return Object.keys(v).length ? v : null;
  }

  async function guardarNota(consulta, campos) {
    try {
      await api.put(`/tenant/clinica/consultas/${consulta.id}`, campos);
      toast.success('Nota guardada.');
      await abrirExpediente(expediente.expediente.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function cerrarNota(consulta) {
    try {
      const { data } = await api.post(`/tenant/clinica/consultas/${consulta.id}/cerrar`);
      toast.success(data.aviso || 'Consulta cerrada.');
      await abrirExpediente(expediente.expediente.id);
      await cargarHoy();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function guardarDx() {
    if (!dx.description.trim()) { toast.error('Falta la descripción del diagnóstico.'); return; }
    try {
      await api.post(`/tenant/clinica/consultas/${dx.encounter_id}/diagnosticos`, {
        description: dx.description,
        icd10_code: dx.icd10_code || null,
        certainty: dx.certainty,
        is_primary: dx.is_primary,
      });
      toast.success('Diagnóstico agregado.');
      setDx(null);
      await abrirExpediente(expediente.expediente.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function emitirReceta() {
    const meds = receta.medicamentos.filter((m) => m.drug_name.trim());
    if (!meds.length) { toast.error('La receta necesita al menos un medicamento.'); return; }
    try {
      const { data } = await api.post(`/tenant/clinica/consultas/${receta.encounter_id}/recetas`, {
        medicamentos: meds,
        practitioner_name: receta.medico || null,
        practitioner_license: receta.cedula || null,
        notes: receta.notas || null,
      });
      toast.success(`Receta ${data.folio} emitida.`);
      setReceta(null);
      await abrirExpediente(expediente.expediente.id);
      await cargarHoy();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function anularReceta(id) {
    const motivo = window.prompt('¿Por qué se anula esta receta? (queda registrado)');
    if (!motivo || motivo.trim().length < 5) {
      if (motivo !== null) toast.error('Escribe el motivo completo.');
      return;
    }
    try {
      const { data } = await api.post(`/tenant/clinica/recetas/${id}/anular`, { motivo });
      toast.success(data.aviso || 'Receta anulada.');
      await abrirExpediente(expediente.expediente.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function guardarCorreccion() {
    if (!correccion.motivo || correccion.motivo.trim().length < 5) {
      toast.error('Escribe por qué corriges la nota anterior.'); return;
    }
    try {
      const { data } = await api.post(
        `/tenant/clinica/consultas/${correccion.encounter_id}/corregir`,
        { motivo: correccion.motivo });
      toast.success(data.aviso || 'Nota de corrección creada.');
      setCorreccion(null);
      await abrirExpediente(expediente.expediente.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function verAccesos() {
    try {
      const { data } = await api.get(
        `/tenant/clinica/expedientes/${expediente.expediente.id}/accesos`);
      setAccesos(data.accesos || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (cargando) {
    return <div className="p-6 text-slate-500">Cargando el consultorio…</div>;
  }

  // ─────────────────────────────── expediente abierto
  if (expediente) {
    const e = expediente.expediente;
    return (
      <div className="p-6 space-y-4 max-w-5xl">
        <Button variant="ghost" onClick={() => setExpediente(null)}>← Volver</Button>

        <div className="border rounded-lg p-4 bg-white space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold">{e.paciente || 'Paciente sin nombre'}</h2>
              <div className="text-sm text-slate-500">
                Expediente {e.folio}
                {e.edad != null && ` · ${e.edad} años`}
                {e.sexo && ` · ${e.sexo}`}
                {e.tipo_sangre && ` · tipo ${e.tipo_sangre}`}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm"
                      onClick={() => navigate(`/tenant/agenda?nueva_cita_lead=${e.crm_lead_id}&nombre=${encodeURIComponent(e.paciente || '')}`)}>
                <Plus className="w-4 h-4 mr-1" /> Agendar cita
              </Button>
              <Button variant="outline" size="sm" onClick={verAccesos}>
                <Eye className="w-4 h-4 mr-1" /> Quién lo ha abierto
              </Button>
            </div>
          </div>

          {/* Lo primero de la ficha, a propósito. */}
          <div className="border-l-4 border-rose-400 pl-3 py-1">
            <Alergias lista={e.alergias} grande />
          </div>
          <Etiquetas titulo="Padecimientos" lista={e.padecimientos_cronicos} />
          <Etiquetas titulo="Medicamentos que toma" lista={e.medicamentos_actuales} />
          {e.contacto_emergencia?.nombre && (
            <div className="text-sm text-slate-600">
              En una emergencia avisar a {e.contacto_emergencia.nombre}
              {e.contacto_emergencia.telefono && ` — ${e.contacto_emergencia.telefono}`}
            </div>
          )}
        </div>

        {accesos && (
          <div className="border rounded-lg p-4 bg-slate-50">
            <div className="font-medium mb-2">Quién abrió este expediente</div>
            {accesos.length === 0 && <div className="text-sm text-slate-500">Sin registros todavía.</div>}
            <ul className="text-sm space-y-1">
              {accesos.map((a, i) => (
                <li key={i} className="text-slate-700">
                  {new Date(a.cuando).toLocaleString()} — {a.quien || 'sin correo'} ({a.accion})
                </li>
              ))}
            </ul>
          </div>
        )}

        {!expediente.consulta_abierta && !nuevaConsulta && (
          <Button onClick={() => setNuevaConsulta({ medico: '', cedula: '', motivo: '' })}>
            <Plus className="w-4 h-4 mr-1" /> Abrir consulta
          </Button>
        )}

        {nuevaConsulta && (
          <div className="border rounded-lg p-4 bg-white space-y-3">
            <div className="font-medium">Nueva consulta</div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Médico que atiende"
                     value={nuevaConsulta.medico}
                     onChange={(ev) => setNuevaConsulta({ ...nuevaConsulta, medico: ev.target.value })} />
              <Input placeholder="Cédula profesional"
                     value={nuevaConsulta.cedula}
                     onChange={(ev) => setNuevaConsulta({ ...nuevaConsulta, cedula: ev.target.value })} />
            </div>
            <Textarea placeholder="¿Por qué viene el paciente?"
                      value={nuevaConsulta.motivo}
                      onChange={(ev) => setNuevaConsulta({ ...nuevaConsulta, motivo: ev.target.value })} />
            <div className="grid grid-cols-4 gap-3">
              <Input placeholder="T/A (120/80)" onChange={(ev) => setNuevaConsulta({ ...nuevaConsulta, ta: ev.target.value })} />
              <Input placeholder="Pulso" onChange={(ev) => setNuevaConsulta({ ...nuevaConsulta, fc: ev.target.value })} />
              <Input placeholder="Temperatura" onChange={(ev) => setNuevaConsulta({ ...nuevaConsulta, temp: ev.target.value })} />
              <Input placeholder="Peso (kg)" onChange={(ev) => setNuevaConsulta({ ...nuevaConsulta, peso: ev.target.value })} />
            </div>
            <div className="flex gap-2">
              <Button onClick={abrirConsulta}>Abrir</Button>
              <Button variant="ghost" onClick={() => setNuevaConsulta(null)}>Cancelar</Button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <h3 className="font-semibold">Historia clínica</h3>
          {expediente.consultas.length === 0 && (
            <div className="text-sm text-slate-500">Este paciente todavía no tiene consultas.</div>
          )}
          {expediente.consultas.map((c) => (
            <Consulta key={c.id} c={c}
                      onGuardar={guardarNota} onCerrar={cerrarNota}
                      onDiagnostico={() => setDx({ encounter_id: c.id, description: '',
                                                   icd10_code: '', certainty: 'presuntivo',
                                                   is_primary: false })}
                      onRecetar={() => setReceta({ encounter_id: c.id, medico: c.medico || '',
                                                   cedula: c.cedula || '', notas: '',
                                                   medicamentos: [{ ...MED_VACIO }] })}
                      onCorregir={() => setCorreccion({ encounter_id: c.id, motivo: '' })}
                      onAnularReceta={anularReceta} />
          ))}
        </div>

        {dx && (
          <Modal titulo="Agregar diagnóstico" onCerrar={() => setDx(null)}>
            <Input placeholder="Diagnóstico" value={dx.description}
                   onChange={(ev) => setDx({ ...dx, description: ev.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Input placeholder="Clave CIE-10 (opcional)" value={dx.icd10_code}
                       onChange={(ev) => setDx({ ...dx, icd10_code: ev.target.value })} />
                <p className="text-xs text-slate-500 mt-1">
                  Si tu consultorio no usa claves, déjalo en blanco.
                </p>
              </div>
              <Select value={dx.certainty} onValueChange={(v) => setDx({ ...dx, certainty: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="presuntivo">Presuntivo (aún por confirmar)</SelectItem>
                  <SelectItem value="definitivo">Definitivo (confirmado)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={dx.is_primary}
                     onChange={(ev) => setDx({ ...dx, is_primary: ev.target.checked })} />
              Es el diagnóstico principal
              <HelpTooltip text="Solo puede haber uno principal por consulta. Si marcas otro, el anterior deja de serlo." />
            </label>
            <Button onClick={guardarDx}>Agregar</Button>
          </Modal>
        )}

        {receta && (
          <Modal titulo="Nueva receta" onCerrar={() => setReceta(null)}>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Médico que receta" value={receta.medico}
                     onChange={(ev) => setReceta({ ...receta, medico: ev.target.value })} />
              <div>
                <Input placeholder="Cédula profesional" value={receta.cedula}
                       onChange={(ev) => setReceta({ ...receta, cedula: ev.target.value })} />
                <p className="text-xs text-slate-500 mt-1">
                  Sin cédula la farmacia no puede surtir la receta.
                </p>
              </div>
            </div>
            {receta.medicamentos.map((m, i) => (
              <div key={i} className="border rounded p-3 space-y-2">
                <Input placeholder="Medicamento (ej. Amoxicilina 500 mg)" value={m.drug_name}
                       onChange={(ev) => cambiarMed(receta, setReceta, i, 'drug_name', ev.target.value)} />
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Dosis (1 cápsula)" value={m.dose}
                         onChange={(ev) => cambiarMed(receta, setReceta, i, 'dose', ev.target.value)} />
                  <Input placeholder="Cada cuánto (cada 8 h)" value={m.frequency}
                         onChange={(ev) => cambiarMed(receta, setReceta, i, 'frequency', ev.target.value)} />
                  <Input placeholder="Por cuánto (7 días)" value={m.duration}
                         onChange={(ev) => cambiarMed(receta, setReceta, i, 'duration', ev.target.value)} />
                </div>
                <Input placeholder="Indicaciones para el paciente" value={m.instructions}
                       onChange={(ev) => cambiarMed(receta, setReceta, i, 'instructions', ev.target.value)} />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={m.is_controlled}
                         onChange={(ev) => cambiarMed(receta, setReceta, i, 'is_controlled', ev.target.checked)} />
                  Es un medicamento controlado
                  <HelpTooltip text="Los controlados quedan marcados en la receta para que puedas identificarlos después." />
                </label>
              </div>
            ))}
            <Button variant="outline" size="sm"
                    onClick={() => setReceta({ ...receta, medicamentos: [...receta.medicamentos, { ...MED_VACIO }] })}>
              <Plus className="w-4 h-4 mr-1" /> Otro medicamento
            </Button>
            <Textarea placeholder="Notas de la receta (opcional)" value={receta.notas}
                      onChange={(ev) => setReceta({ ...receta, notas: ev.target.value })} />
            <Button onClick={emitirReceta}>Emitir receta</Button>
          </Modal>
        )}

        {correccion && (
          <Modal titulo="Corregir una nota cerrada" onCerrar={() => setCorreccion(null)}>
            <p className="text-sm text-slate-600">
              La nota original se conserva tal cual. Esta corrección queda ligada a
              ella, y las dos juntas cuentan lo que pasó.
            </p>
            <Textarea placeholder="¿Qué hay que corregir y por qué?" value={correccion.motivo}
                      onChange={(ev) => setCorreccion({ ...correccion, motivo: ev.target.value })} />
            <Button onClick={guardarCorreccion}>Crear nota de corrección</Button>
          </Modal>
        )}
      </div>
    );
  }

  // ─────────────────────────────── tablero
  return (
    <div className="p-6 space-y-5 max-w-5xl">
      <div className="flex items-center gap-2">
        <Stethoscope className="w-6 h-6 text-teal-600" />
        <h1 className="text-2xl font-semibold">Consultorio</h1>
      </div>

      <SectionHelp
        module="expediente"
        title="el expediente clínico"
        description={'Aquí vive la historia de cada paciente: sus consultas, sus recetas y '
                     + 'lo que autorizó por escrito. Para abrir uno, usa «Abrir un expediente» '
                     + 'y busca al paciente por su nombre o su teléfono.'}
        whatItDoesNot={'no borra nada. Las consultas cerradas y las recetas anuladas se '
                       + 'conservan siempre, porque un expediente que se puede reescribir no '
                       + 'sirve como constancia de nada.'} />

      {/* Estas tres reglas NO van dentro de la ayuda plegable: son las que
          cambian cómo se usa la pantalla, y una ayuda cerrada no la lee nadie. */}
      <div className="border-l-4 border-teal-400 bg-teal-50/50 pl-3 py-2 text-sm text-slate-700 space-y-1">
        <div>
          <b>Las alergias van en el expediente, no en la consulta.</b> Así se ven
          siempre arriba, sin leer notas viejas para saber si el paciente es alérgico.
          Ojo: «sin alergias registradas» no es lo mismo que «no tiene» — solo quiere
          decir que nadie lo ha preguntado todavía.
        </div>
        <div>
          <b>Una consulta se cierra y ya no se edita.</b> Si algo salió mal, se corrige
          con una nota nueva que apunta a la vieja. Las dos se conservan.
        </div>
        <div>
          <b>Quien abre un expediente queda registrado.</b> Puedes revisar en cualquier
          momento quién lo ha consultado.
        </div>
      </div>

      {!hoy?.expedientes ? (
        <div className="border rounded-lg p-6 bg-slate-50 text-slate-700 space-y-2">
          <div className="font-medium">Todavía no tienes ningún expediente.</div>
          <p className="text-sm">
            Abre el primero aquí mismo: busca al paciente por su nombre o su
            teléfono. Si todavía no está en tus contactos, se da de alta en el
            momento.
          </p>
          <Button onClick={() => setEligiendo(true)}>
            <Plus className="w-4 h-4 mr-1" /> Abrir un expediente
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3">
          <Tarjeta n={hoy.expedientes} t="Expedientes" />
          <Tarjeta n={hoy.consultas_abiertas} t="En consulta ahora" resaltar={hoy.consultas_abiertas > 0} />
          <Tarjeta n={hoy.consultas_cerradas_hoy} t="Consultas de hoy" />
          <Tarjeta n={hoy.recetas_hoy} t="Recetas de hoy" />
        </div>
      )}

      {hoy?.en_consulta?.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold">En consulta ahora</h3>
          {hoy.en_consulta.map((p) => (
            <button key={p.encounter_id}
                    onClick={() => abrirExpediente(p.record_id)}
                    className="w-full text-left border rounded-lg p-3 bg-white hover:bg-slate-50">
              <div className="flex items-center justify-between">
                <span className="font-medium">{p.paciente || 'Paciente'}</span>
                <span className="text-xs text-slate-500">{p.folio}</span>
              </div>
              <Alergias lista={p.alergias} />
            </button>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-2 top-3 text-slate-400" />
            <Input className="pl-8" placeholder="Buscar por nombre o folio"
                   value={busqueda}
                   onChange={(ev) => { setBusqueda(ev.target.value); cargarLista(ev.target.value); }} />
          </div>
          <Button onClick={() => setEligiendo(true)}>
            <Plus className="w-4 h-4 mr-1" /> Abrir un expediente
          </Button>
        </div>

        {eligiendo && (
          <div className="border rounded-lg p-3 bg-white space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">¿De quién es el expediente?</span>
              <button onClick={() => { setEligiendo(false); setBuscaPaciente(''); }}
                      aria-label="Cerrar" className="text-slate-400 hover:text-slate-700">
                <X className="w-4 h-4" />
              </button>
            </div>
            <Input autoFocus placeholder="Nombre o teléfono del paciente"
                   value={buscaPaciente}
                   onChange={(ev) => setBuscaPaciente(ev.target.value)} />
            {pacientes.map((per) => (
              <button key={per.id} disabled={abriendo}
                      onClick={() => abrirDeContacto(per)}
                      className="w-full text-left border rounded p-2 hover:bg-slate-50 disabled:opacity-50">
                <div className="font-medium text-sm">{per.display_name || per.name || 'Sin nombre'}</div>
                <div className="text-xs text-slate-500">{per.phone || per.email || ''}</div>
              </button>
            ))}
            {buscaPaciente.trim() && pacientes.length === 0 && (
              <div className="text-sm text-slate-600 space-y-2">
                <div>Nadie con ese nombre en tus contactos.</div>
                <Button variant="outline" disabled={abriendo} onClick={altaYExpediente}>
                  Dar de alta a <b className="mx-1">{buscaPaciente.trim()}</b> y abrir su expediente
                </Button>
              </div>
            )}
          </div>
        )}
        {lista.map((x) => (
          <button key={x.id} onClick={() => abrirExpediente(x.id)}
                  className="w-full text-left border rounded-lg p-3 bg-white hover:bg-slate-50">
            <div className="flex items-center justify-between">
              <span className="font-medium">{x.paciente}</span>
              <span className="text-xs text-slate-500">{x.folio}</span>
            </div>
            <Alergias lista={x.alergias} />
          </button>
        ))}
      </div>
    </div>
  );
}

function cambiarMed(receta, setReceta, i, campo, valor) {
  const meds = [...receta.medicamentos];
  meds[i] = { ...meds[i], [campo]: valor };
  setReceta({ ...receta, medicamentos: meds });
}

function Tarjeta({ n, t, resaltar }) {
  return (
    <div className={`border rounded-lg p-3 ${resaltar ? 'bg-teal-50 border-teal-200' : 'bg-white'}`}>
      <div className="text-2xl font-semibold">{n ?? 0}</div>
      <div className="text-sm text-slate-500">{t}</div>
    </div>
  );
}

function Modal({ titulo, children, onCerrar }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center p-6 overflow-y-auto z-50">
      <div className="bg-white rounded-lg p-5 w-full max-w-2xl space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{titulo}</h3>
          <Button variant="ghost" size="sm" onClick={onCerrar}><X className="w-4 h-4" /></Button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Consulta({ c, onGuardar, onCerrar, onDiagnostico, onRecetar, onCorregir, onAnularReceta }) {
  // S135 — la receta en papel. Se pide al servidor ya armada: el folio, el
  // médico con su cédula y cada medicamento con dosis, frecuencia y duración
  // tienen que salir exactamente igual que en el expediente, y eso solo lo
  // garantiza armarla en un solo lugar. Una receta anulada el servidor se
  // niega a imprimirla, y aquí se muestra su motivo tal cual.
  const imprimirReceta = async (id) => {
    try {
      const { data } = await api.get(`/tenant/clinica/recetas/${id}/imprimir`);
      abrirParaImprimir(data.html, { titulo: `Receta ${data.folio || ''}` });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [borrador, setBorrador] = useState({
    findings: c.hallazgos || '', plan: c.plan || '', notas: c.notas || '',
  });

  return (
    <div className="border rounded-lg p-4 bg-white space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium">
            {c.fecha ? new Date(c.fecha).toLocaleString() : 'Sin fecha'}
          </span>
          {c.medico && <span className="text-sm text-slate-500"> · {c.medico}</span>}
        </div>
        {c.cerrada ? (
          <span className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-slate-100 text-slate-600">
            <Lock className="w-3 h-3" /> Cerrada
          </span>
        ) : (
          <span className="text-xs px-2 py-1 rounded bg-teal-100 text-teal-800">Abierta</span>
        )}
      </div>

      {c.es_correccion_de && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          Esta nota corrige una anterior. La original se conserva íntegra.
        </div>
      )}

      {c.motivo && <div className="text-sm"><b>Motivo:</b> {c.motivo}</div>}

      {c.cerrada ? (
        <>
          {c.hallazgos && <div className="text-sm"><b>Hallazgos:</b> {c.hallazgos}</div>}
          {c.plan && <div className="text-sm"><b>Plan:</b> {c.plan}</div>}
          {c.notas && <div className="text-sm"><b>Notas:</b> {c.notas}</div>}
          <p className="text-xs text-slate-500">
            Esta nota ya no se puede editar. Si hay que corregirla, se hace con una
            nota nueva que queda ligada a esta.
          </p>
          <Button variant="outline" size="sm" onClick={onCorregir}>Corregir nota</Button>
        </>
      ) : (
        <>
          <Textarea placeholder="Exploración y hallazgos" value={borrador.findings}
                    onChange={(ev) => setBorrador({ ...borrador, findings: ev.target.value })} />
          <Textarea placeholder="Plan de manejo" value={borrador.plan}
                    onChange={(ev) => setBorrador({ ...borrador, plan: ev.target.value })} />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => onGuardar(c, borrador)}>Guardar nota</Button>
            <Button size="sm" variant="outline" onClick={onDiagnostico}>
              <FileText className="w-4 h-4 mr-1" /> Diagnóstico
            </Button>
            <Button size="sm" variant="outline" onClick={onRecetar}>
              <Pill className="w-4 h-4 mr-1" /> Receta
            </Button>
            <Button size="sm" variant="secondary" onClick={() => onCerrar(c)}>
              Cerrar consulta
            </Button>
          </div>
        </>
      )}

      {c.signos_vitales && Object.keys(c.signos_vitales).length > 0 && (
        <div className="text-sm text-slate-600">
          {c.signos_vitales.ta && `T/A ${c.signos_vitales.ta} · `}
          {c.signos_vitales.fc && `Pulso ${c.signos_vitales.fc} · `}
          {c.signos_vitales.temp && `Temp ${c.signos_vitales.temp}° · `}
          {c.signos_vitales.peso && `${c.signos_vitales.peso} kg`}
        </div>
      )}

      {c.diagnosticos?.length > 0 && (
        <div className="text-sm space-y-1">
          <b>Diagnósticos</b>
          {c.diagnosticos.map((d) => (
            <div key={d.id} className="text-slate-700">
              {d.principal && <span className="text-xs px-1 rounded bg-teal-100 text-teal-800 mr-1">principal</span>}
              {d.descripcion}
              {d.clave && <span className="text-slate-400"> ({d.clave})</span>}
              <span className="text-slate-400"> — {d.certeza}</span>
            </div>
          ))}
        </div>
      )}

      {c.recetas?.length > 0 && (
        <div className="text-sm space-y-2">
          <b>Recetas</b>
          {c.recetas.map((r) => (
            <div key={r.id} className={`border rounded p-2 ${r.anulada ? 'opacity-60' : ''}`}>
              <div className="flex items-center justify-between">
                <span>
                  {r.folio}
                  {r.tiene_controlados && (
                    <span className="ml-2 text-xs px-1 rounded bg-amber-100 text-amber-800">
                      con controlados
                    </span>
                  )}
                  {r.anulada && (
                    <span className="ml-2 text-xs px-1 rounded bg-rose-100 text-rose-700">anulada</span>
                  )}
                </span>
                {!r.anulada && (
                  <span className="flex items-center gap-1">
                    <HelpTooltip text="Abre la receta lista para imprimir en hoja carta, con el folio, tu nombre y cédula, y cada medicamento con su dosis. Es la que se lleva el paciente a la farmacia.">
                      <Button variant="outline" size="sm" onClick={() => imprimirReceta(r.id)}>Imprimir</Button>
                    </HelpTooltip>
                    <Button variant="ghost" size="sm" onClick={() => onAnularReceta(r.id)}>Anular</Button>
                  </span>
                )}
              </div>
              {r.anulada && r.motivo_anulacion && (
                <div className="text-xs text-slate-500">Motivo: {r.motivo_anulacion}</div>
              )}
              <ul className="text-slate-700 mt-1">
                {r.medicamentos.map((m) => (
                  <li key={m.id}>
                    {m.medicamento} — {m.dosis}, {m.frecuencia}
                    {m.duracion && `, ${m.duracion}`}
                  </li>
                ))}
              </ul>
              {r.vigente_hasta && (
                <div className="text-xs text-slate-500 mt-1">Vigente hasta {r.vigente_hasta}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
